/**
 * Runtime configuration. The same precedence the terminal uses (persisted setting → build-time env →
 * localhost default), and deliberately the same key names: a player who has both clients open is one
 * person with one server, and two settings stores called different things would be a bug they hit on
 * their first day rather than on their tenth.
 */
import type { SettingsStore } from '@core';

export const DEFAULT_SERVER_URL = 'http://localhost:5001';

/** Stamped into sys.hello and X-Client-Version; keep in step with package.json. */
export const CLIENT_VERSION = '0.1.0';

/**
 * Which entry of the server's version manifest describes us.
 *
 * It is `web`, not a new type, and that is a decision rather than an oversight: this is the same
 * browser bundle family, gated by the same forced-update rule, and minting a second client type would
 * mean the server had to learn about a skin. The protocol's `clientType` is about the update gate, not
 * about the render layer.
 */
export const CLIENT_TYPE = 'web';

export interface AppConfig {
  serverUrl: string;
  wsUrl: string;
  clientVersion: string;
  language: string;
  platform: string;
}

/** Derive the /ws endpoint from an http(s) base URL. */
export function toWsUrl(serverUrl: string): string {
  const url = new URL(serverUrl);
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  url.pathname = url.pathname.replace(/\/$/, '') + '/ws';
  return url.toString();
}

function envServerUrl(): string | undefined {
  const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env;
  return env?.VITE_SERVER_URL;
}

export function resolveConfig(settings: SettingsStore, browserLanguage: string): AppConfig {
  const serverUrl = settings.getServerUrl(envServerUrl() ?? DEFAULT_SERVER_URL);
  const language = settings.getLanguage(browserLanguage.startsWith('ru') ? 'ru' : 'en');
  return {
    serverUrl,
    wsUrl: toWsUrl(serverUrl),
    clientVersion: CLIENT_VERSION,
    language,
    platform: 'web',
  };
}
